// The site: the landing page (/), logging in (/login, /reset), your account and place on the waitlist (/account), the
// information pages, and each game, show or event's page (/games/<id>…). The games aren't open to the public: nothing
// on the site links into one, and only invited beta testers (BETA_EMAILS on the server) get past /play; everyone
// else is sent to their account, where the waitlist is. The game's engine is its own chunk, loaded only for them. In
// development the test scripts' old address (/?room=…) still goes straight into Chandigarh, without an account.
import { lazy, Suspense, useEffect } from 'react';
import { logOut, useAccount } from './account';
import { Account } from './AccountPage';
import { CITIES } from './brand';
import { experienceAt } from './catalog';
import { ExperiencePage } from './Experience';
import { InfoPage, PAGES } from './Info';
import { Landing } from './landing/Landing';
import { Login } from './Login';
import { navigate, useLocation } from './router';
import { RegisterHost } from './Register';
import './site.css';

const Game = lazy(() => import('../ui/App').then((m) => ({ default: m.App })));
// the map of cities (with India's whole outline), for beta testers at /play only: not in the homepage's code
const IndiaMap = lazy(() => import('./IndiaMap').then((m) => ({ default: m.IndiaMap })));

export function Site() {
  return (
    <>
      <Page />
      <RegisterHost />
    </>
  );
}

function Page() {
  const { path, search } = useLocation();
  const me = useAccount();
  const legacy = import.meta.env.DEV && path === '/' && new URLSearchParams(search).has('room');
  const city = path.startsWith('/play/') ? CITIES.find((c) => c.id === path.slice(6).replace(/\/$/, '')) : null;
  const playing = path === '/play' || path.startsWith('/play/');

  useEffect(() => {
    if (!playing || me === undefined) return;
    // a tester logs in first; anyone else who isn't invited goes to their place on the waitlist
    if (me === null) navigate(`/login?next=${encodeURIComponent(path)}`, true);
    else if (!me.beta) navigate('/account', true);
    else if (path.startsWith('/play/') && (!city || city.status !== 'live')) navigate('/play', true);
  }, [playing, me, path, city]);

  const xp = experienceAt(path);
  useEffect(() => {
    document.title =
      path === '/account' ? 'Your account · GG Bharat' : city ? `${city.game} · GG Bharat` : xp ? `${xp.title}: ${xp.tagline} · GG Bharat` : 'GG Bharat — India’s worlds, built to play';
  }, [path, city, xp]);

  if (legacy)
    return (
      <Suspense fallback={<GameLoading name="Chandigarh" />}>
        <Game />
      </Suspense>
    );
  // the account isn't known yet, or you're on your way to log in or to your account
  if (playing && !me?.beta) return null;
  if (city?.status === 'live' && me)
    return (
      <Suspense fallback={<GameLoading name={city.game} />}>
        <Game
          preset={{ displayName: me.displayName, instagram: me.instagram, guest: false }}
          title={city.game}
          onExit={() => (location.href = '/play')}
          onLogout={() => void logOut().then(() => (location.href = '/'))}
        />
      </Suspense>
    );
  if (path === '/login' || path === '/reset') return <Login />;
  if (path === '/account') return <Account />;
  if (path === '/play')
    return (
      <Suspense fallback={<GameLoading name="GG Bharat" />}>
        <IndiaMap />
      </Suspense>
    );
  if (xp) return <ExperiencePage e={xp} />;
  const info = path.replace(/\/$/, '');
  if (PAGES[info]) return <InfoPage path={info} />;
  return <Landing />;
}

/** while the game's code arrives: the same dark screen its own loader starts from */
function GameLoading({ name }: { name: string }) {
  return (
    <div className="gg-boot" role="status">
      <div className="gg-boot-title">{name}</div>
      <div className="gg-boot-bar">
        <i />
      </div>
    </div>
  );
}
